import _ from 'lodash';
import {
  getInputAndIntermediateIds,
  toTemplateInputsFromTable,
  toTemplateIntermediatesFromTable,
} from '@/tableDefinitionEditor/templateColumn.utilities';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { captureInputRestrictionType } from '@/tools/models/outputItemMetadata.utilities';
import { resetTemplate, setTemplate } from '@/tools/models/template.actions';
import { saveDraft } from '@/tools/models/template.api';
import { invalidateTemplateLists } from '@/tools/models/template.queries';
import { TEMPLATE_TYPES } from '@/tools/models/template.types';
import {
  filterUnusedFeatures,
  getDerivedFeatureId,
  getFeatureIdFromInput,
  hasFeatureId,
  isFeatureTypeDerivedFeatureWithColumnDefinition,
  isFeatureUsedByOutputs,
  isScopedToRuleInput,
  mapRuleInputsToFeatures,
  omitScopedTo,
  reindexExistingDerivedFeatures,
  toFormulaCreatorRule,
} from '@/tools/models/templateFormatting.utilities';
import { formulaReferencesVariable } from '@/utilities/formulaVariables.utilities';
import { successToast } from '@/utilities/toast.utilities';
import { getNextDefaultName } from '@/utilities/utilities';
import { fetchTemplates } from '@/workbook/workbook.actions';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { TypeEnum as TemplateRuleInputV1ObjectTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { QueryClient } from '@tanstack/react-query';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type {
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  OutputItemMetadataFields,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateInputIds,
  TemplateOutput,
  TemplatePayload,
  TemplateRuleInputWithFeatureId,
  TemplateType,
} from '@/tools/models/template.types';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { TemplateFormulaCreatorRuleV1 } from '@seeqdev/api/model/TemplateFormulaCreatorRuleV1';
import type { TemplateInputV1 } from '@seeqdev/api/model/TemplateInputV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';

/** Given a set of output columns, recursively finds all input columns and intermediate columns (those that are neither
 * inputs nor outputs) and returns updated lists of inputs and outputs.
 * @param outputColumns The new output columns to generate inputs and intermediates from
 * @param inputs The existing list of inputs to avoid duplicates
 * @param existingOutputs The existing list of outputs that should be preserved
 * @param tableColumns The full list of columns in the table
 */
export const getUpdatedInputsAndOutputs = (
  outputColumns: ColumnDefinition[] | DerivedFeatureV1[],
  inputs: TemplateInput[] | InputFeatureV1[],
  existingOutputs: TemplateOutput[],
  tableColumns: ColumnDefinition[],
) => {
  if (!outputColumns?.length && !existingOutputs?.length) {
    if (inputs.length) {
      setTemplate(TEMPLATE_TYPES.SCALING, { inputFeatures: [], derivedFeatures: [] });
    }
    return;
  }

  const columnIdMap = new Map<string, ColumnDefinition>(tableColumns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id).filter((id): id is string => !!id);

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(col.id as string, [], inputIds, intermediateIds, outputIds, columnIdMap, false);
  });

  // TODO CRAB-61003: Remove cast when edit flow is migrated to NewlyAddedInputFeature in CRAB-XXXXX
  const newInputs = [
    ...(inputs as NewlyAddedInputFeature[]),
    ...toTemplateInputsFromTable(inputIds, inputs as NewlyAddedInputFeature[], columnIdMap),
  ];
  const foundInputs: string[] = [];
  const uniqueInputs: NewlyAddedInputFeature[] = newInputs.filter((i) => {
    const ref = i.referenceId?.toLowerCase();
    if (!ref) return true;
    if (foundInputs.includes(ref)) return false;
    foundInputs.push(ref);
    return true;
  });

  const newIntermediateNames = toTemplateIntermediatesFromTable(intermediateIds, columnIdMap, [], []).map(
    (intermediate) => intermediate.name,
  );
  const matchingIntermediateColumns = _.chain(tableColumns)
    .filter((col) => newIntermediateNames.includes(col.displayName))
    .map((col) => ({ ...col, isOutput: false }))
    .uniqBy('id')
    .value();

  const newOutputs = _.uniqBy(
    [...outputColumns, ...existingOutputs, ...matchingIntermediateColumns],
    (f) => f.id ?? '',
  );

  return { inputs: uniqueInputs, outputs: newOutputs };
};

/** Determines if a feature is used by any output derived features.
 *
 * @param featureId - The ID of the feature to check
 * @param derivedFeatures - The current list of derived features in the template
 * @param checkingInputs - True to check only inputs, false to check both outputs and intermediates
 * @returns True if the intermediate is used by any outputs, false otherwise
 */
export const isFeatureUsedByOtherOutputs = (
  featureId: string,
  derivedFeatures: Array<DerivedFeatureV1 | NewlyAddedDerivedFeature>,
  checkingInputs = false,
) => {
  const isStringArray = (a: unknown[]): boolean => a.every((v) => typeof v === 'string');

  return derivedFeatures.some((feature) => {
    if (feature.id === featureId || (feature.isOutput === false && !checkingInputs)) {
      return false;
    }
    return feature.rules?.some((rule) => {
      const inputs = rule.inputs as unknown;

      if (Array.isArray(inputs)) {
        if (rule.inputs.some((input) => typeof input === 'object')) {
          return inputs.some((i) => i.inputFeatureId === featureId);
        }
        if (isStringArray(inputs)) {
          return inputs.includes(featureId);
        }
        return false;
      }
      return false;
    });
  });
};

/** Converts an intermediate to an output if convertToIntermediate is false, otherwise converts an output to an
 *  intermediate
 *
 *  @param featureIdToConvert - The ID of the derived feature to convert
 *  @param derivedFeatures - The current list of derived features in the template
 *  @param convertToIntermediate - True to convert to intermediate, false to convert to output
 *  @param templateType - The type of template being edited, used to determine which store to update
 *  */
export const convertDerivedFeature = (
  featureIdToConvert: string,
  derivedFeatures: DerivedFeatureV1[],
  convertToIntermediate = false,
  templateType: TemplateType = TEMPLATE_TYPES.SCALING,
) => {
  const getDerivedFeatureId = (feature: DerivedFeatureV1) =>
    'featureId' in feature && feature.featureId ? feature.featureId : feature.id;

  const updatedDerivedFeatures = derivedFeatures.map((feature) =>
    getDerivedFeatureId(feature) === featureIdToConvert ? { ...feature, isOutput: !convertToIntermediate } : feature,
  ) as unknown as TemplateOutput[];

  setTemplate(templateType, { derivedFeatures: updatedDerivedFeatures });
};

/**
 * Edits a template by updating all of its template details.
 */
// TODO(CRAB-58652): Extract to template.api.ts — network call, not a pure utility.
export const editTemplate = async (templateId: string, input: TemplateInputV1) => {
  const { data: templateOutput } = await sqTemplatesApi.editTemplate(input, { templateId });
  return templateOutput;
};

const getDerivedFeatureNameForRuleSave = (feature: TemplateOutput): string => {
  if ('displayName' in feature && feature.displayName) {
    return String(feature.displayName);
  }
  return feature.name ?? '';
};

const splitColumnRuleParameters = (parameters: string | undefined): string[] => {
  if (!parameters) {
    return [];
  }
  return parameters.includes('|') ? parameters.split('|') : parameters.split(',');
};

const getExistingFormulaCreatorParameterNames = (formulaCreator: TemplateFormulaCreatorRuleV1): string[] => {
  const parameters = formulaCreator.parameters?.argument;
  return Array.isArray(parameters) ? parameters : [];
};

const getEditedFormulaCreatorParameterNames = (editedRule: ColumnRuleOutputV1): string[] =>
  splitColumnRuleParameters(editedRule.arguments?.parameters);

/** Resolves the FormulaCreator name from preview `ColumnRuleOutputV1` rules (static or column-bound). */
const getEditedFormulaCreatorNameArgument = (
  editedRule: ColumnRuleOutputV1 | undefined,
  tableColumns: ColumnDefinition[],
  fallbackName: string | undefined,
): string | undefined => {
  if (!editedRule) {
    return fallbackName;
  }

  const boundNameColumnId = editedRule.boundArguments?.name;
  if (boundNameColumnId) {
    const boundColumn = tableColumns.find((column) => column.id === boundNameColumnId);
    return boundColumn?.displayName ?? boundColumn?.columnName ?? editedRule.arguments?.name ?? fallbackName;
  }

  return editedRule.arguments?.name ?? fallbackName;
};

const haveSameParameterNames = (existing: string[], edited: string[]): boolean =>
  existing.length === edited.length && existing.every((name, index) => name === edited[index]);

/** A derived feature from the legacy list or from the existing features, for matching rule inputs by reference. */
type ReferencedDerivedFeature = TemplateOutput | TemplateDerivedFeature;

const getColumnReferenceFromModelFeature = (
  feature: TemplateInput | ReferencedDerivedFeature | undefined,
): string | undefined => {
  if (!feature) {
    return undefined;
  }
  const columnDefinitionId = 'columnDefinition' in feature ? feature.columnDefinition?.id : undefined;
  if (columnDefinitionId) {
    return columnDefinitionId;
  }
  if ('referenceId' in feature && feature.referenceId) {
    return feature.referenceId;
  }
  return undefined;
};

const findInputFeatureByReference = (referenceId: string, inputFeatures: TemplateInput[]): TemplateInput | undefined =>
  inputFeatures.find(
    (feature) =>
      feature.id === referenceId || feature.referenceId === referenceId || feature.columnDefinition?.id === referenceId,
  );

const findDerivedFeatureByReference = (
  referenceId: string,
  derivedFeatures: ReferencedDerivedFeature[],
): ReferencedDerivedFeature | undefined =>
  derivedFeatures.find(
    (feature) =>
      feature.id === referenceId ||
      ('referenceId' in feature && feature.referenceId === referenceId) ||
      ('featureId' in feature && feature.featureId === referenceId) ||
      (isFeatureTypeDerivedFeatureWithColumnDefinition(feature) && feature.columnDefinition.id === referenceId),
  );

/** Resolves a feature id to the column the feature is bound to, or undefined when no feature or column has the id. */
const resolveFeatureIdReference = (
  featureId: string,
  inputFeatures: TemplateInput[],
  derivedFeatures: ReferencedDerivedFeature[],
  tableColumns: ColumnDefinition[],
): string | undefined => {
  const matchingInput = findInputFeatureByReference(featureId, inputFeatures);
  if (matchingInput) {
    return getColumnReferenceFromModelFeature(matchingInput) ?? matchingInput.id;
  }

  const matchingDerived = findDerivedFeatureByReference(featureId, derivedFeatures);
  if (matchingDerived) {
    const columnReference = getColumnReferenceFromModelFeature(matchingDerived);
    if (columnReference) {
      return columnReference;
    }
    if ('featureId' in matchingDerived && typeof matchingDerived.featureId === 'string') {
      return matchingDerived.featureId;
    }
    return matchingDerived.id;
  }

  return tableColumns.some((column) => column.id === featureId) ? featureId : undefined;
};

const resolveExistingRuleInputReference = (
  input: TemplateRuleInputV1Object,
  inputFeatures: TemplateInput[],
  derivedFeatures: ReferencedDerivedFeature[],
  tableColumns: ColumnDefinition[],
): string | undefined => {
  const isLiteralInput =
    input.argument !== undefined &&
    input.inputFeatureId === undefined &&
    input.inputFeatureIndex === undefined &&
    input.derivedFeatureIndex === undefined &&
    !hasFeatureId(input);
  if (isLiteralInput) {
    return `literal:${String(input.argument)}`;
  }

  // An input that tracks the feature it uses by featureId is resolved by that id first: its index can point into a
  // different list than `inputFeatures` or `derivedFeatures`, e.g. an existing feature's index into the new features.
  const trackedFeatureId = hasFeatureId(input) && input.inputFeatureId === undefined ? input.featureId : undefined;
  const trackedReference = trackedFeatureId
    ? resolveFeatureIdReference(trackedFeatureId, inputFeatures, derivedFeatures, tableColumns)
    : undefined;
  if (trackedReference) {
    return trackedReference;
  }

  if (input.inputFeatureIndex != null) {
    const inputFeature = inputFeatures[input.inputFeatureIndex];
    return getColumnReferenceFromModelFeature(inputFeature) ?? inputFeature?.id;
  }

  if (input.derivedFeatureIndex != null) {
    const derivedFeature = derivedFeatures[input.derivedFeatureIndex];
    const columnReference = getColumnReferenceFromModelFeature(derivedFeature);
    if (columnReference) {
      return columnReference;
    }
    if (derivedFeature && 'featureId' in derivedFeature && typeof derivedFeature.featureId === 'string') {
      return derivedFeature.featureId;
    }
    return derivedFeature?.id;
  }

  const inputFeatureId = getFeatureIdFromInput(input);
  if (inputFeatureId) {
    return resolveFeatureIdReference(inputFeatureId, inputFeatures, derivedFeatures, tableColumns) ?? inputFeatureId;
  }

  return undefined;
};

const resolveExistingRuleInputReferences = (
  existingRule: FeatureRuleV1,
  inputFeatures: TemplateInput[],
  derivedFeatures: ReferencedDerivedFeature[],
  tableColumns: ColumnDefinition[],
): (string | undefined)[] =>
  (existingRule.inputs ?? []).map((input) =>
    resolveExistingRuleInputReference(input as TemplateRuleInputV1Object, inputFeatures, derivedFeatures, tableColumns),
  );

const resolveEditedRuleInputReferences = (editedRule: ColumnRuleOutputV1): string[] =>
  (editedRule.inputs ?? []).map((inputId) => String(inputId));

const haveSameResolvedInputReferences = (
  existingReferences: (string | undefined)[],
  editedReferences: string[],
): boolean =>
  existingReferences.length === editedReferences.length &&
  existingReferences.every((reference, index) => reference !== undefined && reference === editedReferences[index]);

const isRuleWithFormulaCreator = (
  rule: ColumnRuleOutputV1,
): rule is ColumnRuleOutputV1 & { formulaCreator: TemplateFormulaCreatorRuleV1 } =>
  'formulaCreator' in rule &&
  !!rule.formulaCreator &&
  typeof rule.formulaCreator === 'object' &&
  'formula' in rule.formulaCreator &&
  'name' in rule.formulaCreator &&
  'parameters' in rule.formulaCreator;

const normalizeFormulaCreatorNameForSave = (
  formulaCreator: TemplateFormulaCreatorRuleV1,
  featureName: string,
): TemplateFormulaCreatorRuleV1 => {
  const nameInput = formulaCreator.name;
  const hasValidNameReference =
    nameInput?.argument != null ||
    nameInput?.inputFeatureId != null ||
    nameInput?.inputFeatureIndex != null ||
    nameInput?.derivedFeatureIndex != null;

  if (hasValidNameReference) {
    return formulaCreator;
  }

  return {
    ...formulaCreator,
    name: {
      name: 'name',
      description: 'name argument for FormulaCreator',
      type: OutputTypeEnum.TEXT,
      argument: featureName,
    },
  };
};

/** Converts rules for a newly created derived feature into the format expected by the backend
 *
 * @param rules - The rules to convert
 * @param inputFeatures - The current list of input features in the template
 * @param derivedFeatures - The current list of derived features in the template
 * @param newFeature - The newly created derived feature
 * @param scopedTo - The scopedTo value for the template
 * */
export const convertNewRules = (
  rules: ColumnRuleOutputV1[],
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  newFeature: TemplateOutput,
) => {
  const featureName = getDerivedFeatureNameForRuleSave(newFeature);

  return rules.map((rule) => {
    // The scope input comes back on read and is re-added by appserver on save, so it is dropped rather than
    // resolved as if it were one of the template's own inputs.
    const ruleInputs = ((rule.inputs ?? []) as Array<string | TemplateRuleInputV1Object>).filter(
      (input) => !isScopedToRuleInput(input),
    );
    const mappedInputs = mapRuleInputsToFeatures(
      ruleInputs,
      inputFeatures,
      derivedFeatures,
      TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
    );

    if (isRuleWithFormulaCreator(rule)) {
      return {
        inputs: mappedInputs,
        formulaCreator: omitScopedTo(normalizeFormulaCreatorNameForSave(rule.formulaCreator, featureName)),
      };
    }

    // No scope is written: it is bound when the template is applied.
    return {
      inputs: mappedInputs,
      formulaCreator: {
        formula: {
          name: 'formula',
          description: 'formula argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: rule.arguments.formula,
        },
        parameters: {
          name: 'parameters',
          description: 'parameters argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: splitColumnRuleParameters(rule.arguments.parameters),
        },
        name: {
          name: 'name',
          description: 'name argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: featureName,
        },
      },
    };
  });
};

/**
 * Derived features materialized from table column selection during edit-template.
 * These are not persisted template features and must not be sent as existing features on save.
 */
export const isTableSourcedDerivedFeature = (feature: DerivedFeatureV1): boolean => {
  if ('displayName' in feature) {
    return true;
  }
  if ('featureId' in feature) {
    return true;
  }
  // Intermediates from walking table dependencies only carry referenceId (column id), not a template feature id.
  if ('referenceId' in feature && feature.referenceId && !feature.id) {
    return true;
  }
  return false;
};

/** Rebuilds template inputs from derived feature rule references, preserving persisted input feature ids. */
export const reconcileTemplateInputFeaturesForDerivedFeatures = (
  derivedFeatures: DerivedFeatureV1[],
  columns: ColumnDefinition[],
  inputFeatures: TemplateInput[],
): TemplateInput[] =>
  updateInputs(
    derivedFeatures,
    columns,
    inputFeatures.filter((feature) => !feature?.referenceId) as TemplateInput[],
  ) as TemplateInput[];

const resolveTemplateFeatureIdForSave = (feature: DerivedFeatureV1): string | undefined => {
  if ('featureId' in feature && feature.featureId) {
    return feature.featureId as string;
  }
  return feature.id;
};

const resolveTemplateFeatureCanonicalIdForSave = (
  feature: DerivedFeatureV1,
  existingDerivedFeatures?: TemplateDerivedFeature[],
): string | undefined => {
  if (feature.canonicalId) {
    return feature.canonicalId;
  }
  const templateFeatureId = resolveTemplateFeatureIdForSave(feature);
  if (!templateFeatureId || !existingDerivedFeatures?.length) {
    return undefined;
  }
  return existingDerivedFeatures.find((existingFeature) => existingFeature.id === templateFeatureId)?.canonicalId;
};

/** Formats the template for saving by converting new derived features and inputs into the expected backend format */
export function formatTemplateForSave({
  name,
  description,
  templateId,
  scopedTo,
  derivedFeatures,
  inputFeatures,
  existingDerivedFeatures,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string | undefined;
  derivedFeatures: DerivedFeatureV1[];
  inputFeatures: InputFeatureV1[];
  /** Baseline template features used to recover canonicalId when the working copy lost it (e.g. after column edit). */
  existingDerivedFeatures?: TemplateDerivedFeature[];
}): TemplateInputV1 {
  const newIntermediates = derivedFeatures.filter((feature) => feature.isOutput === false && 'displayName' in feature);
  const newDerived = derivedFeatures.filter(
    (feature) =>
      (feature.isOutput === undefined && 'displayName' in feature) ||
      (feature.isOutput === true && 'featureId' in feature),
  );
  const existingFeatures = derivedFeatures?.length
    ? derivedFeatures.filter((feature) => !isTableSourcedDerivedFeature(feature))
    : [];

  const sortInputFeatures = (features: InputFeatureV1[]) => [
    ...features.filter((feature) => !('mappedFeatureId' in feature)),
    ...features.filter((feature) => 'mappedFeatureId' in feature),
  ];

  const sortedInputFeaturesForSave = sortInputFeatures(inputFeatures);

  // Rule input indices must match the inputFeatures array sent to the backend, which includes the
  // instance name input even though formulas never reference it.
  const ruleIndexingInputFeatures = sortedInputFeaturesForSave;

  const reindexedExistingFeatures = reindexExistingDerivedFeatures(
    existingFeatures as TemplateDerivedFeature[],
    ruleIndexingInputFeatures as TemplateInput[],
  );

  // Order is important: intermediates first, then new derived outputs, then existing features, otherwise we get an
  // out-of-bounds error when converting rules that reference other derived features
  const orderedDerivedFeatures = [...newIntermediates, ...newDerived, ...reindexedExistingFeatures];

  const formattedDerivedFeatures = (orderedDerivedFeatures as DerivedFeatureV1[])
    .map((feature) => {
      if (
        ('rules' in feature && Array.isArray(feature.rules) && 'displayName' in feature) ||
        feature.isOutput !== undefined
      ) {
        const newRules = convertNewRules(
          feature.rules as unknown as ColumnRuleOutputV1[],
          ruleIndexingInputFeatures as TemplateInput[],
          orderedDerivedFeatures as unknown as TemplateOutput[],
          feature as unknown as TemplateOutput,
        );
        const templateFeatureId = resolveTemplateFeatureIdForSave(feature);
        const canonicalId = resolveTemplateFeatureCanonicalIdForSave(feature, existingDerivedFeatures);
        return {
          name: 'displayName' in feature ? feature.displayName : feature.name,
          rules: newRules,
          outputType: OutputTypeEnum.ITEMID,
          isOutput: feature?.isOutput !== undefined ? feature.isOutput : true,
          id: templateFeatureId,
          ...(canonicalId && { canonicalId }),
          ...(feature.outputItemType && { outputItemType: feature.outputItemType }),
          ...(feature.outputItemMetadata && { outputItemMetadata: feature.outputItemMetadata }),
        };
      }
      return undefined;
    })
    .filter(Boolean);

  const inputIds = (sortedInputFeaturesForSave as InputFeatureV1[]).map((feature) => feature.id).filter(Boolean);

  const formattedInputFeatures = (sortedInputFeaturesForSave as InputFeatureV1[])
    .filter((feature) => {
      if ('mappedFeatureId' in feature && feature.mappedFeatureId) {
        return !inputIds.includes(feature.mappedFeatureId as string);
      }
      return true;
    })
    .map((feature) => {
      // Resolved once per feature (rather than only inside the referenceId branch below) so the
      // fallback branch at the end can also recover a persisted restriction.
      const tableSourcedInput = feature as TemplateInput;
      const persistedMatch = tableSourcedInput.referenceId
        ? (inputFeatures as TemplateInput[]).find(
            (input) =>
              input.id &&
              (input.referenceId === tableSourcedInput.referenceId ||
                (tableSourcedInput.name && input.name === tableSourcedInput.name) ||
                (tableSourcedInput.name && input.name === tableSourcedInput.originalName)),
          )
        : undefined;
      const restrictionOutputItemType = feature.outputItemType ?? persistedMatch?.outputItemType;
      const restrictionOutputItemMetadata = feature.outputItemMetadata ?? persistedMatch?.outputItemMetadata;
      const restrictionFields = {
        ...(restrictionOutputItemType !== undefined && { outputItemType: restrictionOutputItemType }),
        ...(restrictionOutputItemMetadata !== undefined && { outputItemMetadata: restrictionOutputItemMetadata }),
      };

      if (feature.id && feature.outputType !== undefined) {
        return feature;
      }
      if ('inputFeatureId' in feature && feature.inputFeatureId) {
        return {
          description: feature.description,
          name: feature.name,
          outputType: OutputTypeEnum.ITEMID,
          id: feature.inputFeatureId,
          ...restrictionFields,
        };
      }
      if ('referenceId' in feature && feature.referenceId) {
        if (persistedMatch?.id) {
          return {
            description: feature.description ?? persistedMatch.description,
            name: feature.name ?? persistedMatch.name,
            outputType: OutputTypeEnum.ITEMID,
            id: persistedMatch.id,
            ...(persistedMatch.canonicalId && { canonicalId: persistedMatch.canonicalId }),
            ...restrictionFields,
          };
        }
      }
      return {
        description: feature.description,
        name: feature.name,
        outputType: OutputTypeEnum.ITEMID,
        ...restrictionFields,
      };
    });

  return {
    name,
    description,
    id: templateId,
    scopedTo,
    derivedFeatures: formattedDerivedFeatures,
    inputFeatures: formattedInputFeatures,
  } as unknown as TemplateInputV1;
}
export function getUpdatedIntermediates(
  intermediateIds: string[],
  derivedFeatures: DerivedFeatureV1[],
): DerivedFeatureV1[] {
  const unusedIds = intermediateIds.filter(
    (intermediateId) => !isFeatureUsedByOtherOutputs(intermediateId, derivedFeatures),
  );

  return derivedFeatures.filter((feature) => !unusedIds.includes(feature.id!));
}

/** Gets all input IDs used by a derived feature's rules
 *
 * @param removingFeature - The derived feature to get input IDs from
 * @returns An array of input IDs used by the derived feature
 */
export function getRemovingFeatureInputIds(removingFeature?: DerivedFeatureV1): string[] {
  return (
    removingFeature?.rules?.flatMap((rule) =>
      Array.isArray(rule.inputs)
        ? rule.inputs
            .map((input) =>
              typeof input === 'object' && input.inputFeatureId
                ? input.inputFeatureId
                : typeof input === 'string'
                  ? input
                  : undefined,
            )
            .filter((id): id is string => Boolean(id))
        : [],
    ) ?? []
  );
}

/** Determines if a given input ID is used by any derived features other than the one being removed.
 *
 * @param inputId - The ID of the input to check
 * @param derivedFeatures - The current list of derived features in the template
 * @param removingFeatureId - The ID of the derived feature being removed
 * @returns True if the input is used by other derived features, false otherwise
 */
export function isInputUsedElsewhere(
  inputId: string,
  derivedFeatures: DerivedFeatureV1[],
  removingFeatureId: string,
): boolean {
  return derivedFeatures.some(
    (feature) =>
      feature.id !== removingFeatureId &&
      feature.rules?.some((rule) =>
        Array.isArray(rule.inputs)
          ? rule.inputs.some(
              (input) =>
                (typeof input === 'object' && input.inputFeatureId === inputId) ||
                (typeof input === 'string' && input === inputId),
            )
          : false,
      ),
  );
}

/** Updates the template's inputs by collecting all unique inputs from the derived features' rules,
 *  excluding any inputs that are also intermediates or that belong to a feature being removed, and are not used by
 *  other features.
 *
 * @param derivedFeatures - The current list of derived features in the template
 * @param columns - The full list of columns in the table
 * @param inputFeatures - The existing list of input features
 * @param removingFeature - An optional derived feature that is being removed
 * @returns The updated list of template inputs
 */
export function updateInputs(
  derivedFeatures: DerivedFeatureV1[],
  columns: ColumnDefinition[],
  inputFeatures: TemplateInput[],
  removingFeature?: DerivedFeatureV1,
): TemplateInput[] {
  // Collect all inputs from derived features' rules
  const allInputs = derivedFeatures.flatMap(
    (feature) =>
      feature.rules?.flatMap((rule) =>
        Array.isArray(rule.inputs)
          ? rule.inputs
              .map((input) => {
                if (typeof input === 'object' && input.inputFeatureId) {
                  return input;
                } else if (typeof input === 'string') {
                  const col = columns.find((c) => c.id === input);
                  if (col) {
                    return {
                      referenceId: col.id,
                      name: col.displayName,
                      isCollapsed: false,
                      description: col.description ?? '',
                      originalName: col.displayName,
                    };
                  }
                }
                return undefined;
              })
              .filter(Boolean)
          : [],
      ) ?? [],
  );

  // Remove inputs that belong to the removing feature and are not used elsewhere
  const removingFeatureInputIds = removingFeature ? getRemovingFeatureInputIds(removingFeature) : [];
  const remainingInputs = allInputs.filter((input) => {
    if (!input || typeof input !== 'object') return false;
    const inputId = (input as any).inputFeatureId ?? (input as any).referenceId;
    if (!inputId) return false;
    if (!removingFeatureInputIds.includes(inputId) || !removingFeature?.id) return true;
    return isInputUsedElsewhere(inputId, derivedFeatures, removingFeature.id);
  });

  // Remove inputs that are also intermediates
  const derivedNames = derivedFeatures.map((feature) =>
    'columnName' in feature && feature.columnName ? feature.columnName : feature.name,
  );
  const derivedIds = derivedFeatures.map((feature) =>
    'featureId' in feature && feature.featureId ? feature.featureId : feature.id,
  );
  const filteredInputs = remainingInputs.filter(
    (input) =>
      !(
        (input && 'inputFeatureId' in input && input.inputFeatureId && derivedIds.includes(input.inputFeatureId)) ||
        (input && 'referenceId' in input && input.referenceId && derivedIds.includes(input.referenceId)) ||
        (input?.name && derivedNames.includes(input.name))
      ),
  );

  // If a new input has an input feature id that matches an existing input feature, preserve the existing input feature
  // properties
  filteredInputs.forEach((input, index: number) => {
    const matchingInputFeature = inputFeatures.find((feature) =>
      'id' in feature && input !== undefined && 'inputFeatureId' in input
        ? feature.id === input?.inputFeatureId
        : false,
    );
    if (matchingInputFeature) {
      filteredInputs[index] = { ...matchingInputFeature } as unknown as TemplateRuleInputV1Object;
    }
  });

  // Preserve properties of existing inputs by replacing entries in filteredInputs with their counterparts from
  // inputFeatures when they refer to the same underlying table column. Match by `referenceId` (table-sourced
  // inputs synthesized above) or `columnDefinition.id` (template input features fetched/enriched with the
  // table application mapping in the edit-template flow).
  const mergedInputs = (filteredInputs as TemplateInput[]).map((input) => {
    if (!input.referenceId) {
      return input;
    }
    const inputColumnDefinitionId = (feature: TemplateInput) =>
      (feature as TemplateInput & { columnDefinition?: { id?: string } }).columnDefinition?.id;
    const existing = inputFeatures.find(
      (f) =>
        f.referenceId === input.referenceId ||
        ('columnDefinition' in f && inputColumnDefinitionId(f) === input.referenceId),
    );
    return existing ? existing : input;
  });

  return _.uniqBy(
    [
      // Put new items with a referenceId at the top of the list so they are easier to find in the UI
      ...mergedInputs.filter((input) => !!input.referenceId),
      ...mergedInputs.filter((input) => !input.referenceId),
    ],
    (input: TemplateInputIds) => input.inputFeatureId || input.referenceId || input.id,
  ) as unknown as TemplateInput[];
}

/** True for persisted template intermediate features during edit-template (not table-sourced column selections). */
const isPersistedTemplateIntermediateForEdit = (feature: TemplateOutput): boolean =>
  feature.isOutput === false &&
  feature.id !== undefined &&
  (!('displayName' in feature) ||
    'showInIntermediates' in feature ||
    'featureId' in feature ||
    'columnDefinition' in feature);

/**
 * Preserves the existing feature's template-format rules (`FeatureRuleV1` with `formulaCreator`,
 * resolved `scopedTo`, and `inputFeatureId` references on inputs) when the user's column edit
 * didn't alter the rule logic — only column-level metadata like the display name.
 *
 * The preview API returns `ColumnRuleOutputV1`-shaped rules that omit the `formulaCreator`
 * template object. If we replaced the existing feature's rules with those, the subsequent
 * template save would (1) fail with "Exactly one of argument, inputFeatureId, inputFeatureIndex,
 * or derivedFeatureIndex must be set" because `convertNewRules` would rebuild `scopedTo` from
 * the (possibly undefined) template-level scopedTo, and (2) lose the rule-input → input-feature
 * binding, surfacing previously-mapped inputs as "New input" in the UI.
 *
 * Returns `undefined` when preservation isn't safe (rule count differs, formula text changed,
 * input count changed, parameter list changed, input references changed, etc.), so the caller
 * falls back to the prior behavior of using the edited column's rules.
 */
export const tryPreserveTemplateRulesForRename = (
  existingRules: FeatureRuleV1[] | undefined,
  editedRules: ColumnRuleOutputV1[] | undefined,
  newName: string | undefined,
  inputFeatures: TemplateInput[],
  derivedFeatures: ReferencedDerivedFeature[],
  tableColumns: ColumnDefinition[],
): FeatureRuleV1[] | undefined => {
  if (!existingRules?.length || !editedRules?.length || existingRules.length !== editedRules.length) {
    return undefined;
  }

  const isTemplateFormulaCreatorRule = (
    rule: FeatureRuleV1 | undefined,
  ): rule is FeatureRuleV1 & { formulaCreator: TemplateFormulaCreatorRuleV1 } =>
    !!rule && 'formulaCreator' in rule && !!rule.formulaCreator && typeof rule.formulaCreator === 'object';

  // Only preserve for true rename-only edits: same rule type, formula text, input count,
  // parameter list, and resolved input references. Other structural changes (input swap/rebind,
  // added parameter, etc.) fall through to the regular flow.
  const canPreserve = existingRules.every((existingRule, i) => {
    const editedRule = editedRules[i];
    if (!isTemplateFormulaCreatorRule(existingRule) || !editedRule) {
      return false;
    }
    const existingFormula = existingRule.formulaCreator.formula?.argument;
    const editedFormula = editedRule.arguments?.formula;
    if (existingFormula !== editedFormula) {
      return false;
    }

    const existingInputCount = existingRule.inputs?.length ?? 0;
    const editedInputCount = editedRule.inputs?.length ?? 0;
    if (existingInputCount !== editedInputCount) {
      return false;
    }

    const existingParameters = getExistingFormulaCreatorParameterNames(existingRule.formulaCreator);
    const editedParameters = getEditedFormulaCreatorParameterNames(editedRule);
    if (!haveSameParameterNames(existingParameters, editedParameters)) {
      return false;
    }

    const existingInputReferences = resolveExistingRuleInputReferences(
      existingRule,
      inputFeatures,
      derivedFeatures,
      tableColumns,
    );
    const editedInputReferences = resolveEditedRuleInputReferences(editedRule);
    return haveSameResolvedInputReferences(existingInputReferences, editedInputReferences);
  });

  if (!canPreserve) {
    return undefined;
  }

  return existingRules.map((rule, ruleIndex) => {
    if (!isTemplateFormulaCreatorRule(rule)) {
      return rule;
    }
    const nameInput = rule.formulaCreator.name;
    // Only retarget the name argument when it was a literal value tied to the column. If the name
    // references an input/derived feature (inputFeatureId/inputFeatureIndex/derivedFeatureIndex),
    // the binding is intentional and should not be overwritten by the column rename.
    const isLiteralName = nameInput?.argument != null;
    const preservedNameArgument = getEditedFormulaCreatorNameArgument(editedRules[ruleIndex], tableColumns, newName);
    if (!preservedNameArgument || !isLiteralName) {
      return rule;
    }
    return {
      ...rule,
      formulaCreator: {
        ...rule.formulaCreator,
        name: { ...nameInput, argument: preservedNameArgument },
      },
    };
  });
};

// TODO CRAB-56692: delete once save uses formatTemplateInput and nothing reads derivedFeatures/inputFeatures;
// getEditedExistingFeatureUpdate covers the same edit for the existing features.
/**
 * Works out the Scaling Table template's derivedFeatures and inputFeatures after the column of one of its derived
 * features is edited, recalculating inputs and intermediates accordingly, without setting them.
 *
 * @returns undefined if there is no edited column or no derived feature with that id
 */
export function getEditedDerivedFeatureUpdate(
  featureId: string | undefined,
  derivedFeatures: TemplateOutput[],
  inputFeatures: TemplateInput[],
  editedColumn: ColumnDefinition | undefined,
  tableColumns: ColumnDefinition[],
): TemplatePayload | undefined {
  if (!featureId || !editedColumn) {
    return;
  }

  const featureIndex = derivedFeatures.findIndex(
    (feature) => feature.id === featureId || ('featureId' in feature && feature.featureId === featureId),
  );
  if (featureIndex === -1) {
    return;
  }

  const existingFeature = derivedFeatures[featureIndex];
  if (isPersistedTemplateIntermediateForEdit(existingFeature)) {
    const existingPersistedFeature = existingFeature as unknown as DerivedFeatureV1 & {
      showInIntermediates?: boolean;
      columnDefinition?: Pick<ColumnDefinition, 'id'>;
    };
    const newIntermediateName = editedColumn.displayName ?? editedColumn.columnName ?? existingPersistedFeature.name;
    const preservedIntermediateRules = tryPreserveTemplateRulesForRename(
      existingPersistedFeature.rules as FeatureRuleV1[] | undefined,
      editedColumn.rules as ColumnRuleOutputV1[] | undefined,
      newIntermediateName,
      inputFeatures,
      derivedFeatures,
      tableColumns,
    );
    const editedIntermediate = {
      ...existingPersistedFeature,
      name: newIntermediateName,
      rules:
        preservedIntermediateRules ??
        ((editedColumn.rules ?? existingPersistedFeature.rules) as unknown as FeatureRuleV1[]),
      showInIntermediates: true,
      ...(isFeatureTypeDerivedFeatureWithColumnDefinition(existingPersistedFeature) && {
        columnDefinition: { id: editedColumn.id },
      }),
    };

    const updatedDerivedFeatures = [
      ...derivedFeatures.slice(0, featureIndex),
      editedIntermediate,
      ...derivedFeatures.slice(featureIndex + 1),
    ] as TemplateOutput[];

    const finalInputs = exposedForTesting.updateInputs(
      updatedDerivedFeatures as unknown as DerivedFeatureV1[],
      tableColumns,
      inputFeatures as unknown as TemplateInput[],
    );

    return {
      derivedFeatures: updatedDerivedFeatures,
      inputFeatures: finalInputs as unknown as TemplateInput[],
    };
  }

  const existingOutputAsDerived = existingFeature as unknown as DerivedFeatureV1;
  const existingOutputName = existingOutputAsDerived.name;
  const newOutputName = editedColumn.displayName ?? editedColumn.columnName ?? existingOutputName;
  const preservedOutputRules = tryPreserveTemplateRulesForRename(
    existingOutputAsDerived.rules as FeatureRuleV1[] | undefined,
    editedColumn.rules as ColumnRuleOutputV1[] | undefined,
    newOutputName,
    inputFeatures,
    derivedFeatures,
    tableColumns,
  );

  // Rename-only edit (formula and dependencies unchanged): update the existing output in place,
  // preserving its identity, existing (non-table-sourced) shape, and array position. Reordering it
  // to the front or giving it a table-sourced `displayName` would make formatTemplateForSave bucket
  // it as a "new derived" feature ahead of the existing intermediates it depends on, emitting a
  // forward derivedFeatureIndex the backend can't build (CRAB-58787). A real formula/structure edit
  // (preservedOutputRules == null) still uses the recompute path below, since deps may have changed.
  if (preservedOutputRules) {
    const renamedOutput = {
      ...existingOutputAsDerived,
      name: newOutputName,
      rules: preservedOutputRules,
    };
    const updatedDerivedFeatures = [
      ...derivedFeatures.slice(0, featureIndex),
      renamedOutput,
      ...derivedFeatures.slice(featureIndex + 1),
    ] as TemplateOutput[];
    const finalInputs = exposedForTesting.updateInputs(
      updatedDerivedFeatures as unknown as DerivedFeatureV1[],
      tableColumns,
      inputFeatures as unknown as TemplateInput[],
    );
    return {
      derivedFeatures: updatedDerivedFeatures,
      inputFeatures: finalInputs as unknown as TemplateInput[],
    };
  }

  // preservedOutputRules is always null here (the rename-only case returned above), so the edited
  // column's rules are used as-is — this branch handles genuine formula/structure edits.
  const editedFeature = {
    ...editedColumn,
    featureId,
    isOutput: derivedFeatures[featureIndex].isOutput,
    ...(existingOutputAsDerived.canonicalId && { canonicalId: existingOutputAsDerived.canonicalId }),
  };

  const updatedDerivedFeatures = [
    ...derivedFeatures.slice(0, featureIndex),
    editedFeature,
    ...derivedFeatures.slice(featureIndex + 1),
  ] as TemplateOutput[];

  const editedOutputsAndTheirIntermediates = exposedForTesting.getUpdatedIntermediates(
    updatedDerivedFeatures.filter((f) => 'isOutput' in f && f.isOutput === false).map((f) => f.id) as string[],
    updatedDerivedFeatures as unknown as DerivedFeatureV1[],
  );

  const updatedInputsAndOutputs = getUpdatedInputsAndOutputs(
    [editedColumn],
    inputFeatures,
    editedOutputsAndTheirIntermediates as unknown as TemplateOutput[],
    tableColumns,
  );

  const formattedOutputs = updatedInputsAndOutputs?.outputs?.length
    ? updatedInputsAndOutputs.outputs.map((f) =>
        'isOutput' in f && f.isOutput === false ? { ...f, showInIntermediates: true } : f,
      )
    : [];

  // Get the formatted version of the edited column to make sure it has the isOutput field set correctly
  const formattedEditedColumn = updatedDerivedFeatures.find((f) => f.id === editedFeature.id);
  const allDerivedFeatures = [formattedEditedColumn, ...formattedOutputs];
  const finalDerivedFeatures = _.uniqBy(allDerivedFeatures, (f) => f?.id ?? '') as unknown as TemplateOutput[];
  const finalInputs = exposedForTesting.updateInputs(
    finalDerivedFeatures as unknown as DerivedFeatureV1[],
    tableColumns,
    inputFeatures as unknown as TemplateInput[],
  );

  return {
    derivedFeatures: finalDerivedFeatures,
    inputFeatures: finalInputs as unknown as TemplateInput[],
  };
}

export function mapDerivedFeature(
  intermediateId: string,
  isMapped: boolean,
  mappedFeatureId: string | undefined,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] {
  return newDerivedFeatures.map((feature) => {
    if ((feature.referenceId ?? feature.id) !== intermediateId) return feature;
    return isMapped
      ? { ...feature, isMapped: true, mappedFeatureId }
      : { ...feature, isMapped: false, mappedFeatureId: undefined };
  });
}

/**
 * Saves the current edits to the draft without locking or publishing.
 * The draft remains editable for further changes.
 */
// TODO(CRAB-58652): Extract to editModel.actions.ts — side-effecting orchestration, not a pure utility.
export async function handleSaveDraftInToolPane(
  templateId: string,
  versionId: string,
  formattedTemplate: TemplateInputV1,
  workbookId: string,
  queryClient: QueryClient,
) {
  await saveDraft(templateId, versionId, formattedTemplate);
  successToast({ messageKey: 'TEMPLATES.SAVE_DRAFT_SUCCESS' });
  resetTemplate(TEMPLATE_TYPES.TOOL);
  setActiveTool(TREND_TOOLS.MODELS);
  invalidateTemplateLists(queryClient);
  await fetchTemplates([workbookId]);
}

export interface ExistingOutputRemoval {
  /** The template fields to set */
  templateUpdate: TemplatePayload;
  /** The existing derived features that were dropped: the output itself and any intermediates nothing uses any more.
   *  Empty when the output was kept as an intermediate. */
  removedExistingDerivedFeatures: TemplateDerivedFeature[];
}

/**
 * Works out the template features after removing an existing output, without setting them. If another output still
 * uses it, it becomes an intermediate. Otherwise it is removed, along with any features nothing uses any more.
 *
 * @returns undefined if there is no existing derived feature with that id
 */
export function getExistingOutputRemoval(
  outputId: string,
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): ExistingOutputRemoval | undefined {
  if (!existingDerivedFeatures?.length) return;

  const featureToRemove = existingDerivedFeatures.find((f) => f.id === outputId);
  if (!featureToRemove) return;

  // Convert to intermediate if another output still uses it, directly or through intermediates. New features reference
  // existing ones by inputFeatureId, so they count too.
  const remainingExisting = existingDerivedFeatures.filter((f) => f.id !== outputId);
  if (isFeatureUsedByOutputs(outputId, remainingExisting, newDerivedFeatures, newInputFeatures)) {
    return {
      templateUpdate: {
        existingDerivedFeatures: existingDerivedFeatures.map((feature) =>
          feature.id === outputId ? { ...feature, isOutput: false } : feature,
        ),
      },
      removedExistingDerivedFeatures: [],
    };
  }

  // Remove the output and use the unified removal function to clean up unused dependencies
  const allInUseFeatures = filterUnusedFeatures(
    remainingExisting,
    existingInputFeatures,
    newDerivedFeatures,
    newInputFeatures,
  );

  const keptExistingIds = new Set(allInUseFeatures.usedExistingDerivedFeatures.map((f) => f.id));
  return {
    templateUpdate: {
      existingDerivedFeatures: allInUseFeatures.usedExistingDerivedFeatures,
      existingInputFeatures: allInUseFeatures.usedExistingInputFeatures,
      newDerivedFeatures: allInUseFeatures.usedNewDerivedFeatures,
      newInputFeatures: allInUseFeatures.usedNewInputFeatures,
    },
    removedExistingDerivedFeatures: existingDerivedFeatures.filter((f) => !keptExistingIds.has(f.id)),
  };
}

/** Depending on whether the feature being updated is a newly added feature that has not been saved yet or an
 *  existing feature, updates the correct list of features and recalculates dependencies accordingly by updating
 *  inputs and derived features. */
export const updateInputFeature = (
  patch: Partial<TemplateInput>,
  input: TemplateInput,
  newInputFeatures: NewlyAddedInputFeature[],
  existingInputFeatures: TemplateInput[],
  isNewlyAdded = false,
  templateType: TemplateType = TEMPLATE_TYPES.TOOL,
) => {
  const inputId = input.referenceId ?? input.id;
  if (!inputId) {
    return;
  }
  if (isNewlyAdded) {
    const updatedNewFeatures = newInputFeatures.map((feature) =>
      feature.referenceId === inputId ? { ...feature, ...patch } : feature,
    );
    setTemplate(templateType, { newInputFeatures: updatedNewFeatures });
    return;
  }
  const updatedExistingFeatures = existingInputFeatures.map((feature) =>
    feature.id === inputId ? { ...feature, ...patch } : feature,
  );
  setTemplate(templateType, {
    existingInputFeatures: updatedExistingFeatures,
  });
};

/** Converts newly added derived features into options to be displayed in the Formula Item Table
 *
 * @param features - The newly added features to convert to options
 * @param isOutput - Whether to format outputs (true) or intermediates (false)
 */
const formatNewlyAddedFeaturesForDisplay = (
  features: NewlyAddedDerivedFeature[],
  isOutput: boolean,
): FormulaItemTableOption[] => {
  return features
    .filter((feature) => {
      if (isOutput) {
        return feature.isOutput;
      }
      return !feature.isOutput && !feature.intermediateAsInput;
    })
    .map((f) => {
      const allFormulas = (f.rules ?? []).map((rule) => {
        const formula = rule.formulaCreator?.formula?.argument ?? '';
        const paramArgs: string[] = Array.isArray(rule.formulaCreator?.parameters?.argument)
          ? rule.formulaCreator.parameters.argument
          : [];
        const inputs: TemplateRuleInputV1Object[] = Array.isArray(rule.inputs) ? rule.inputs : [];

        const parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
          return {
            name: input.name,
            identifier: paramArgs[idx] ?? input.name ?? '',
          };
        });
        return { formula, parameters };
      });

      return {
        key: isOutput ? f.id! : f.referenceId!,
        display: f.name,
        allFormulas,
        isNew: true,
        isMapped: f.isMapped,
        mappedFeatureId: f.mappedFeatureId,
        outputItemType: f.outputItemType,
        outputItemMetadata: f.outputItemMetadata,
      };
    });
};

/** Converts newly added intermediate features into options to be displayed in the Formula Item Table
 * @param features - The newly added features to convert to options
 */
export const formatNewlyAddedIntermediatesForDisplay = (
  features: NewlyAddedDerivedFeature[],
): FormulaItemTableOption[] => formatNewlyAddedFeaturesForDisplay(features, false);

/** Converts newly added output features into options to be displayed in the Formula Item Table
 * @param features - The newly added features to convert to options
 */
export const formatNewlyAddedOutputsForDisplay = (features: NewlyAddedDerivedFeature[]): FormulaItemTableOption[] =>
  formatNewlyAddedFeaturesForDisplay(features, true);

/**
 * Updates an existing derived feature with new formula and parameter mappings.
 * Maps parameters to proper rule inputs with correct indices for input/derived features.
 * @param featureToEdit The existing derived feature being edited
 * @param newFormula The new formula to update the feature with
 * @param parameterMappings The selected parameter mappings from the formula editor, containing the mapped items and their corresponding formula parameter identifiers
 * @param allNewInputs All newly added input features in the template, used to find the correct indices for mapped input features
 * @param allNewDerived All newly added derived features in the template, used to find the correct indices for mapped derived features
 * @param existingInputFeatures
 * @param existingDerivedFeatures
 * @param newName Optional new name for the feature. If not provided, the original name is retained.
 * @returns An updated TemplateDerivedFeature with recalculated rules based on the new formula and parameter mappings
 */
export function getUpdatedDerivedFeature(
  featureToEdit: TemplateDerivedFeature,
  newFormula: string,
  parameterMappings: FormulaEditorParam[],
  allNewInputs: NewlyAddedInputFeature[],
  allNewDerived: NewlyAddedDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
  newName?: string,
  outputMetadata?: OutputItemMetadataFields,
): TemplateDerivedFeature {
  const featureName = newName ?? featureToEdit.name;
  const originalInputs = featureToEdit.rules?.[0]?.inputs ?? [];

  return {
    ...featureToEdit,
    ...(newName && { name: newName }),
    ...outputMetadata,
    rules: [
      {
        ...featureToEdit.rules?.[0],
        inputs: createFeatureInputs(
          parameterMappings,
          allNewInputs,
          allNewDerived,
          originalInputs,
          existingInputFeatures,
          existingDerivedFeatures,
        ),
        formulaCreator: toFormulaCreatorRule(
          {
            formula: newFormula,
            parameters: parameterMappings.map((param) => ({
              name: param.identifier,
              item: { id: param.item.id },
            })),
          },
          featureName,
        ),
      },
      ...(featureToEdit.rules?.slice(1) ?? []),
    ],
  };
}

/**
 * Updates an existing derived feature's rule indices to match the current input/derived arrays.
 * This is needed when features are edited multiple times and the arrays change.
 */
export const updateExistingDerivedFeatureIndices = <T extends TemplateDerivedFeature | NewlyAddedDerivedFeature>(
  feature: T,
  currentNewInputFeatures: NewlyAddedInputFeature[],
  currentNewDerivedFeatures: NewlyAddedDerivedFeature[],
  previousNewInputFeatures: NewlyAddedInputFeature[],
  previousNewDerivedFeatures: NewlyAddedDerivedFeature[],
): T => {
  if (!feature.rules?.length) return feature;

  const getFeatureId = (feature: { referenceId?: string; id?: string } | undefined) =>
    feature?.referenceId ?? feature?.id;

  const findNewIndex = <T extends { referenceId?: string; id?: string }>(
    oldIndex: number | undefined,
    previousArray: T[],
    currentArray: T[],
  ): number | undefined => {
    if (oldIndex === undefined) return undefined;

    const previousFeatureId = getFeatureId(previousArray[oldIndex]);
    if (!previousFeatureId) return undefined;

    const newIndex = currentArray.findIndex((f) => getFeatureId(f) === previousFeatureId);
    return newIndex >= 0 ? newIndex : undefined;
  };

  return {
    ...feature,
    rules: feature.rules.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        const newInputIndex = findNewIndex(input.inputFeatureIndex, previousNewInputFeatures, currentNewInputFeatures);
        const newDerivedIndex = findNewIndex(
          input.derivedFeatureIndex,
          previousNewDerivedFeatures,
          currentNewDerivedFeatures,
        );

        // Remove old indices and conditionally add new ones

        const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;

        return {
          ...baseInput,
          ...(newInputIndex !== undefined && { inputFeatureIndex: newInputIndex }),
          ...(newDerivedIndex !== undefined && { derivedFeatureIndex: newDerivedIndex }),
        };
      }),
    })),
  };
};

/**
 * Filters parameter mappings to only those actually used in a formula.
 * Whole-token, case-insensitive matching avoids partial identifier matches (e.g., $temp won't
 * match $temperature) while still counting case-divergent references like $Temp for `temp`.
 */
export function filterUsedParameters(parameterMappings: FormulaEditorParam[], formula: string): FormulaEditorParam[] {
  return parameterMappings.filter(
    (param) => !!param.identifier && formulaReferencesVariable(formula, param.identifier),
  );
}

/**
 * Creates input mappings with indices for a derived feature rule.
 * Maps parameter item IDs to input/derived feature indices (for new features)
 * or inputFeatureId (for existing features).
 *
 * For parameters that don't have indices in new arrays, sets inputFeatureId
 * so existing features are tracked as used and not filtered out.
 */
export function createFeatureInputs(
  parameters: FormulaEditorParam[],
  allNewInputs: NewlyAddedInputFeature[],
  allNewDerived: NewlyAddedDerivedFeature[],
  originalInputs: TemplateRuleInputWithFeatureId[] = [],
  existingInputFeatures: TemplateInput[] = [],
  existingDerivedFeatures: TemplateDerivedFeature[] = [],
): TemplateRuleInputWithFeatureId[] {
  const inputIndexByRefId = new Map(allNewInputs.map((f, index) => [f.referenceId, index]));
  const derivedIndexByRefId = new Map(allNewDerived.map((f, index) => [f.referenceId ?? f.id, index]));

  // Tracks which features are already persisted so we can use inputFeatureId instead of creating input or derived
  // feature indices
  const existingInputFeatureIds = new Set([
    ...originalInputs.map((input) => input.inputFeatureId).filter((id): id is string => !!id),
    ...existingInputFeatures.map((f) => f.id),
    ...existingDerivedFeatures.map((f) => f.id),
  ]);

  return parameters.map((param) => {
    const inputIndex = inputIndexByRefId.get(param.item.id) ?? -1;
    const derivedIndex = derivedIndexByRefId.get(param.item.id) ?? -1;
    const templateFeatureId =
      param.inputFeatureId && existingInputFeatureIds.has(param.inputFeatureId) ? param.inputFeatureId : undefined;

    return {
      name: param.item.name,
      type: TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
      featureId: param.item.id,
      ...(inputIndex >= 0 && !templateFeatureId && { inputFeatureIndex: inputIndex }),
      ...(derivedIndex >= 0 && !templateFeatureId && { derivedFeatureIndex: derivedIndex }),
      ...(templateFeatureId && { inputFeatureId: templateFeatureId }),
    };
  });
}

/**
 * Formats a new output feature based on a formula, creating the appropriate formula creator rule and mapping parameter dependencies to input/derived feature indices.
 *
 * @param formula - The formula string
 * @param outputName - The name for the output feature
 * @param usedParams - Parameters used in the formula
 * @param id - The ID for the feature
 * @param newInputs - Input features to reference
 * @param derivedFeatures - Derived features to reference for inputs
 * @param outputMetadata - Optional metadata containing output item type and metadata map
 * @param isOutput - Whether this is an output feature (default: true)
 * @returns A NewlyAddedDerivedFeature with the formula rule
 */
export function formatOutputFeatureFromFormula(
  formula: string,
  outputName: string,
  usedParams: FormulaEditorParam[],
  id: string,
  newInputs: NewlyAddedInputFeature[],
  derivedFeatures: NewlyAddedDerivedFeature[],
  outputMetadata?: OutputItemMetadataFields,
  isOutput = true,
  existingInputFeatures: TemplateInput[] = [],
  existingDerivedFeatures: TemplateDerivedFeature[] = [],
): NewlyAddedDerivedFeature {
  const formulaCreatorRule = toFormulaCreatorRule(
    {
      formula,
      parameters: usedParams.map((param) => ({
        name: param.identifier,
        item: { id: param.item.id },
      })),
    },
    outputName,
  );

  const inputs = createFeatureInputs(
    usedParams,
    newInputs,
    derivedFeatures,
    [],
    existingInputFeatures,
    existingDerivedFeatures,
  );

  return {
    id,
    ...(isOutput ? {} : { referenceId: id }), // Intermediates need referenceId for lookups
    name: outputName,
    isOutput,
    rules: [{ inputs, formulaCreator: formulaCreatorRule }],
    ...outputMetadata,
  };
}

/**
 * Generates a default name for a new output feature.
 * Uses the same naming pattern as "New Column" in Asset Groups.
 * If editing an existing feature, returns the existing name.
 */
export function getDefaultOutputName(
  isCreatingNew: boolean,
  featureToEdit: TemplateDerivedFeature | NewlyAddedDerivedFeature | undefined,
  existingOutputs: NewlyAddedDerivedFeature[],
): string {
  if (!isCreatingNew && featureToEdit) {
    return featureToEdit.name;
  }
  const usedNewOutputNames = existingOutputs.filter((f) => f.isOutput).map((f) => f.name);
  return getNextDefaultName(usedNewOutputNames, 'TEMPLATES.NEW_OUTPUT');
}

function isNewlyAddedDerivedFeature(
  feature: TemplateDerivedFeature | NewlyAddedDerivedFeature,
): feature is NewlyAddedDerivedFeature {
  return 'referenceId' in feature;
}

/** Converts an intermediate to a new input feature representation. */
export function convertIntermediateToInputFeature(
  intermediate: TemplateDerivedFeature | NewlyAddedDerivedFeature,
): NewlyAddedInputFeature {
  const featureId = isNewlyAddedDerivedFeature(intermediate)
    ? (intermediate.referenceId ?? intermediate.id)
    : intermediate.id;
  const outputItemType = intermediate.outputItemType
    ? captureInputRestrictionType(intermediate.outputItemType)
    : undefined;
  return {
    id: featureId!,
    referenceId: featureId!,
    name: intermediate.name,
    originalName: intermediate.name,
    description: '',
    isCollapsed: false,
    ...(outputItemType !== undefined && { outputItemType }),
  };
}

/** Marks an intermediate as toggled to input and handles existing vs new feature differences. */
export function markIntermediateAsInput(
  intermediateId: string,
  newIntermediate: NewlyAddedDerivedFeature | undefined,
  existingIntermediate: TemplateDerivedFeature | undefined,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): { updatedNewDerivedFeatures: NewlyAddedDerivedFeature[]; updatedExistingDerivedFeatures: TemplateDerivedFeature[] } {
  if (newIntermediate) {
    return {
      updatedNewDerivedFeatures: newDerivedFeatures.map((f) =>
        getDerivedFeatureId(f) === intermediateId ? { ...f, intermediateAsInput: true } : f,
      ),
      updatedExistingDerivedFeatures: existingDerivedFeatures,
    };
  }

  if (!existingIntermediate) {
    throw new Error(
      `markIntermediateAsInput: neither newIntermediate nor existingIntermediate provided for ${intermediateId}`,
    );
  }

  // Move existing intermediate to newDerivedFeatures, preserving full metadata for restore
  const convertedIntermediate: NewlyAddedDerivedFeature = {
    id: existingIntermediate.id,
    referenceId: existingIntermediate.id,
    name: existingIntermediate.name,
    rules: existingIntermediate.rules,
    isOutput: false,
    intermediateAsInput: true,
    wasExistingIntermediate: true,
    originalFeature: existingIntermediate,
  };

  return {
    updatedNewDerivedFeatures: [...newDerivedFeatures, convertedIntermediate],
    updatedExistingDerivedFeatures: existingDerivedFeatures.filter((f) => f.id !== intermediateId),
  };
}

/** Collects IDs of all intermediates that have been toggled to inputs. */
export function getToggledIntermediateIds(newDerivedFeatures: NewlyAddedDerivedFeature[]): Set<string> {
  return new Set(
    newDerivedFeatures
      .filter((f) => f.intermediateAsInput)
      .map((f) => f.referenceId ?? f.id)
      .filter((id): id is string => !!id),
  );
}

/**
 * Guarantees a restored intermediate lands in the derived list, not the input list.
 */
export function ensureIntermediateRestored(
  restoredIntermediate: NewlyAddedDerivedFeature,
  newInputs: NewlyAddedInputFeature[],
  newDerived: NewlyAddedDerivedFeature[],
): { newInputs: NewlyAddedInputFeature[]; newDerived: NewlyAddedDerivedFeature[] } {
  const restoredId = getDerivedFeatureId(restoredIntermediate);
  const correctedInputs = newInputs.filter((f) => (f.referenceId ?? f.id) !== restoredId);
  const alreadyDerived = newDerived.some((f) => getDerivedFeatureId(f) === restoredId);
  const correctedDerived = alreadyDerived
    ? newDerived
    : [...newDerived, { ...restoredIntermediate, intermediateAsInput: undefined }];
  return { newInputs: correctedInputs, newDerived: correctedDerived };
}

/** Creates a Map from feature id/referenceId to array index for O(1) lookups. */
function buildInputIndexMap(features: (NewlyAddedInputFeature | TemplateInput)[]): Map<string, number> {
  const map = new Map<string, number>();
  features.forEach((f, index) => {
    if (f.referenceId) map.set(f.referenceId, index);
    if (f.id) map.set(f.id, index);
  });
  return map;
}

/** Updates existing derived features to reference toggled intermediates using inputFeatureIndex. */
export function updateExistingDerivedRefsToToggledIntermediates(
  existingDerivedFeatures: TemplateDerivedFeature[],
  toggledIntermediateIds: Set<string>,
  allInputFeatures: (NewlyAddedInputFeature | TemplateInput)[],
): TemplateDerivedFeature[] {
  const inputIndexById = buildInputIndexMap(allInputFeatures);

  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (typeof input !== 'object') return input;

        const lookupId = input.inputFeatureId || (hasFeatureId(input) ? input.featureId : undefined);

        if (lookupId && toggledIntermediateIds.has(lookupId)) {
          const inputIndex = inputIndexById.get(lookupId);
          if (inputIndex === undefined) {
            return input;
          }

          const { inputFeatureId, derivedFeatureIndex, ...baseInput } = input;
          return { ...baseInput, featureId: lookupId, inputFeatureIndex: inputIndex };
        }
        return input;
      }),
    })),
  }));
}

/**
 * Updates existing derived features when an existing intermediate is restored from input.
 * Converts inputFeatureIndex references to the restored intermediate back to inputFeatureId,
 * and reindexes remaining inputFeatureIndex references.
 */
export function updateExistingDerivedRefsAfterRestore(
  existingDerivedFeatures: TemplateDerivedFeature[],
  restoredIntermediateId: string,
  allInputFeatures: (NewlyAddedInputFeature | TemplateInput)[],
): TemplateDerivedFeature[] {
  const inputIndexById = buildInputIndexMap(allInputFeatures);

  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (typeof input !== 'object') return input;

        const lookupId = input.inputFeatureId || (hasFeatureId(input) ? input.featureId : undefined);

        // If this references the restored intermediate, convert to inputFeatureId
        if (lookupId === restoredIntermediateId) {
          const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;
          return { ...baseInput, inputFeatureId: restoredIntermediateId };
        }

        // If the input has an inputFeatureIndex, reindex it to account for the restored intermediate being removed from inputs
        if (input.inputFeatureIndex !== undefined) {
          const newIndex = lookupId ? inputIndexById.get(lookupId) : undefined;

          if (newIndex === undefined) {
            return input;
          }

          return { ...input, inputFeatureIndex: newIndex };
        }
        return input;
      }),
    })),
  }));
}

export const exposedForTesting = {
  isFeatureUsedByOtherOutputs,
  convertNewRules,
  getUpdatedInputsAndOutputs,
  convertDerivedFeature,
  updateInputs,
  getUpdatedIntermediates,
  editTemplate,
  handleSaveDraftInToolPane,
};
